import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { cachedRequest } from '../utils/apiCache';
import { fetchDateTime } from './dateTimeService';
import { fetchPrayerTimes } from './prayerService';
import { fetchDuas } from './duaService';
import { fetchHadiths } from './hadithService';
import { fetchSurahs } from './quranService';
import logger from '../utils/logger';

// Separate cache durations for different types of data
const CACHE_DURATIONS = {
  PRAYER_TIMES: 12 * 60 * 60 * 1000, // 12 hours
  SURAHS: 7 * 24 * 60 * 60 * 1000,   // 7 days
  DUAS: 24 * 60 * 60 * 1000,         // 24 hours
  HADITHS: 24 * 60 * 60 * 1000,      // 24 hours
  GOALS: 5 * 60 * 1000,              // 5 minutes
  DATE_TIME: 60 * 1000               // 1 minute
};

// Track pending requests to prevent duplicate calls
const pendingRequests = new Map();

const loadDataWithCache = async (key, loader, duration) => {
  // Check if there's a pending request
  if (pendingRequests.has(key)) {
    return pendingRequests.get(key);
  }

  // Create the promise for this request
  const promise = cachedRequest(
    key,
    loader,
    { cacheDuration: duration }
  );

  // Store the promise
  pendingRequests.set(key, promise);

  try {
    const result = await promise;
    return result;
  } finally {
    // Clean up the pending request
    pendingRequests.delete(key);
  }
};

const loadPrayerTimes = async (settings, date = null) => {
  // Early return with clear error if settings, location or coordinates are missing
  if (!settings) {
    logger.warn('Settings object is missing');
    return { prayerTimes: {}, prayerTimesError: "Settings not available" };
  }

  if (!settings?.location || !settings?.coordinates) {
    logger.warn('Missing location or coordinates for prayer times');
    return { prayerTimes: {}, prayerTimesError: "Location not set" };
  }

  try {
    // Get date in YYYY-MM-DD format for the cache key
    const targetDate = date || new Date();
    const dateStr = targetDate.toISOString().split('T')[0];
    
    // Create a cache key based on the settings and date
    const cacheKey = `prayer_times_${settings.coordinates.lat}_${settings.coordinates.lon}_${settings.calculationMethod}_${dateStr}`;
    
    // Check if we should force fresh data
    const shouldForceFresh = () => {
      try {
        // Only apply force fresh logic for today's prayer times
        if (date) return false;
        
        // Try to get cached data from localStorage
        const cachedData = localStorage.getItem(cacheKey);
        if (!cachedData) return true;
        
        const { timestamp } = JSON.parse(cachedData);
        const now = new Date();
        const cachedTime = new Date(timestamp);
        
        // Force refresh if:
        // 1. Cached data is from a different day
        if (cachedTime.toDateString() !== now.toDateString()) return true;
        
        // 2. It's after Isha prayer (use a helper function if available)
        if (typeof window !== 'undefined' && window.haveAllPrayersPassed) {
          const cachedPrayerTimes = JSON.parse(cachedData).data;
          return window.haveAllPrayersPassed(cachedPrayerTimes, now, settings.tunedTimes);
        }
        
        return false;
      } catch (error) {
        logger.error("Error checking if should force fresh prayer times:", error);
        return true;
      }
    };
    
    const forceFresh = shouldForceFresh();

    const prayerTimes = await loadDataWithCache(
      cacheKey,
      () => fetchPrayerTimes({
        location: settings.location,
        coordinates: settings.coordinates,
        calculationMethod: settings.calculationMethod,
        timezone: settings.timezone,
        tunedTimes: settings.tunedTimes,
        forceFresh
      }, date),
      CACHE_DURATIONS.PRAYER_TIMES
    );
    
    // Store in localStorage for easier access
    localStorage.setItem(cacheKey, JSON.stringify({
      data: prayerTimes,
      timestamp: Date.now()
    }));

    return { prayerTimes, prayerTimesError: null };
  } catch (error) {
    logger.error("Error fetching prayer times:", error);
    return { prayerTimes: {}, prayerTimesError: "Error fetching prayer times" };
  }
};

const loadTomorrowPrayerTimes = async (settings) => {
  // Create a date object for tomorrow
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  return loadPrayerTimes(settings, tomorrow);
};

export const fetchDashboardData = async (user, settings, onUpdate, prevDashboard = {}) => {
  if (!user) return null;
  
  const today = new Date().toISOString().split('T')[0];
  const baseKey = `dashboard_${user?.uid || 'anonymous'}_${today}`;
  
  try {
    let dashboard = {};
    let shouldUpdate = false;

    // Load date/time first as it's fastest and most critical
    const dateTime = await loadDataWithCache(
      `${baseKey}_datetime`,
      fetchDateTime,
      CACHE_DURATIONS.DATE_TIME
    );
    
    dashboard = { dateTime };
    // Only update if dateTime is different from previous
    if (onUpdate && (!prevDashboard.dateTime || 
        JSON.stringify(prevDashboard.dateTime) !== JSON.stringify(dateTime))) {
      onUpdate(dashboard);
      shouldUpdate = true;
    }

    // Load today's prayer times
    const { prayerTimes, prayerTimesError } = await loadPrayerTimes(settings);
    
    // Load tomorrow's prayer times
    const { prayerTimes: tomorrowPrayerTimes, prayerTimesError: tomorrowPrayerTimesError } = 
      await loadTomorrowPrayerTimes(settings);
    
    // Accumulate changes
    dashboard = {
      ...dashboard,
      prayerTimes,
      prayerTimesError,
      tomorrowPrayerTimes,
      tomorrowPrayerTimesError,
      location: settings?.location,
      coordinates: settings?.coordinates
    };
    
    // Only add adhkarCompleted if prayerTimes has actual times
    if (prayerTimes && prayerTimes.times && Object.keys(prayerTimes.times).length > 0) {
      dashboard.adhkarCompleted = Object.keys(prayerTimes.times).reduce((acc, prayer) => {
        // Preserve existing completion state if available
        acc[prayer] = prevDashboard.adhkarCompleted?.[prayer] || false;
        return acc;
      }, {});
    }
    
    // Check if prayer times are different from what we already have
    if (onUpdate && (
      !prevDashboard.prayerTimes || 
      JSON.stringify(prevDashboard.prayerTimes) !== JSON.stringify(prayerTimes) ||
      !prevDashboard.tomorrowPrayerTimes || 
      JSON.stringify(prevDashboard.tomorrowPrayerTimes) !== JSON.stringify(tomorrowPrayerTimes)
    )) {
      onUpdate(dashboard);
      shouldUpdate = true;
    }

    // Load non-critical data in parallel
    const [surahs, duas, hadiths, goalsSnap] = await Promise.allSettled([
      loadDataWithCache(
        `${baseKey}_surahs`,
        fetchSurahs,
        CACHE_DURATIONS.SURAHS
      ),
      loadDataWithCache(
        `${baseKey}_duas`,
        fetchDuas,
        CACHE_DURATIONS.DUAS
      ),
      loadDataWithCache(
        `${baseKey}_hadiths`,
        fetchHadiths,
        CACHE_DURATIONS.HADITHS
      ),
      user.uid ? loadDataWithCache(
        `${baseKey}_goals_${user.uid}`,
        () => getDoc(doc(db, 'userGoals', user.uid)),
        CACHE_DURATIONS.GOALS
      ) : Promise.resolve(null)
    ]);

    // Determine indices robustly
    let duasArr = duas.status === 'fulfilled' ? duas.value : [];
    let hadithsArr = hadiths.status === 'fulfilled' ? hadiths.value : [];
    let currentDuaIndex = prevDashboard.currentDuaIndex;
    if (
      typeof currentDuaIndex !== 'number' ||
      !duasArr ||
      currentDuaIndex < 0 ||
      currentDuaIndex >= (duasArr.length || 0)
    ) {
      currentDuaIndex = duasArr.length ? Math.floor(Math.random() * duasArr.length) : 0;
      console.log('[fetchDashboardData] Setting initial currentDuaIndex:', currentDuaIndex);
    } else {
      console.log('[fetchDashboardData] Preserving currentDuaIndex:', currentDuaIndex);
    }
    let currentHadithIndex = prevDashboard.currentHadithIndex;
    if (
      typeof currentHadithIndex !== 'number' ||
      !hadithsArr ||
      currentHadithIndex < 0 ||
      currentHadithIndex >= (hadithsArr.length || 0)
    ) {
      currentHadithIndex = hadithsArr.length ? Math.floor(Math.random() * hadithsArr.length) : 0;
      console.log('[fetchDashboardData] Setting initial currentHadithIndex:', currentHadithIndex);
    } else {
      console.log('[fetchDashboardData] Preserving currentHadithIndex:', currentHadithIndex);
    }

    // Update the accumulated data with the final pieces
    const finalDashboard = {
      ...dashboard,
      surahs: surahs.status === 'fulfilled' ? surahs.value?.chapters || [] : [],
      duas: duasArr,
      currentDuaIndex,
      hadiths: hadithsArr,
      currentHadithIndex,
      goals: goalsSnap.status === 'fulfilled' && goalsSnap.value?.exists && goalsSnap.value.exists() 
        ? goalsSnap.value.data().goals 
        : []
    };
    
    // Final update with all data
    if (onUpdate && (shouldUpdate || 
      !prevDashboard.duas || prevDashboard.duas.length === 0 ||
      !prevDashboard.hadiths || prevDashboard.hadiths.length === 0 ||
      prevDashboard.currentDuaIndex !== currentDuaIndex ||
      prevDashboard.currentHadithIndex !== currentHadithIndex
    )) {
      onUpdate(finalDashboard);
    }

    return finalDashboard;
  } catch (error) {
    logger.error('Error fetching dashboard data:', error);
    throw error;
  }
}; 